/**
 * Convert a HH:mm string to minutes since midnight.
 * @param {string} timeText - Time in HH:mm format.
 * @returns {number} Minutes since midnight.
 */
export function timeToMinutes(timeText) {
    const [hours, minutes] = String(timeText).split(':').map((value) => Number(value));
    return (hours * 60) + minutes;
}

/**
 * Convert minutes since midnight into HH:mm.
 * @param {number} totalMinutes - Minutes since midnight.
 * @returns {string} Time in HH:mm format.
 */
export function minutesToTime(totalMinutes) {
    const safeMinutes = Math.max(0, Math.min(24 * 60, Number(totalMinutes)));
    const hours = Math.floor(safeMinutes / 60);
    const mins = safeMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/**
 * Align a minute value down to the nearest 5-minute boundary.
 * @param {number} minuteValue - Raw minute value.
 * @returns {number} Snapped minute value.
 */
export function snapDownToFive(minuteValue) {
    return Math.floor(Number(minuteValue) / 5) * 5;
}

/**
 * Align a minute value up to the nearest 5-minute boundary.
 * @param {number} minuteValue - Raw minute value.
 * @returns {number} Snapped minute value.
 */
export function snapUpToFive(minuteValue) {
    return Math.ceil(Number(minuteValue) / 5) * 5;
}

/**
 * Build display slot start minutes between day start/end boundaries.
 * @param {string} dayStart - Start time in HH:mm.
 * @param {string} dayEnd - End time in HH:mm.
 * @param {number} slotMins - Display slot minutes.
 * @returns {Array<number>} Slot start minute values.
 */
export function buildDisplaySlots(dayStart, dayEnd, slotMins) {
    const startMinutes = timeToMinutes(dayStart);
    const endMinutes = timeToMinutes(dayEnd);
    const slotSize = Number(slotMins) || 15;

    const slots = [];
    let pointer = startMinutes;
    while (pointer < endMinutes) {
        slots.push(pointer);
        pointer += slotSize;
    }
    return slots;
}

/**
 * Convert a Date object to yyyy-mm-dd using local date parts.
 * @param {Date} dateValue - JavaScript date object.
 * @returns {string} Date in yyyy-mm-dd format.
 */
export function formatDateForApi(dateValue) {
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
